import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  View,
  Text,
  Modal,
  TouchableOpacity,
  FlatList,
  TextInput,
  StyleSheet,
  Platform,
  ActivityIndicator,
  Dimensions,
  Animated,
  Easing,
} from 'react-native';

import { X, Search, RefreshCw, Info } from './../icons';
import { useDealsQuery } from '../../hooks/queries/useDealsQuery';
import { Deal } from '../../types';
import { dateFormatting } from '../../utils/dateFormatting';
import { searchDeals, sortDeals } from '../../utils/dealUtils';

/** Written calendar date in the device locale. Not parsed as UTC midnight. */
const formatWrittenCalendarDate = (value: string): string | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  const utcNoon = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(utcNoon);
};

export const formatDealCreatedDate = (dateString: string): string => {
  const instant = dateFormatting.format(dateString, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
  if (instant) return instant;
  return formatWrittenCalendarDate(dateString) ?? '';
};
import { ANIMATION } from '../../constants/ui';

interface DealSelectionModalProps {
  visible: boolean;
  onClose: () => void;
  onSelect: (deal: Deal) => void;
  title?: string;
  /** Request ID - if provided, verifies user owns the request. Optional for new requests. */
  requestId?: string;
}

type SortOption = 'date' | 'name' | 'company' | 'regNumber';
type SortOrder = 'asc' | 'desc';

export const DealSelectionModal: React.FC<DealSelectionModalProps> = ({
  visible,
  onClose,
  onSelect,
  title = 'Select Deal Registration',
  requestId,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortOption | null>('date');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  const windowWidth = Dimensions.get('window').width;
  const isDesktop = windowWidth >= 1024;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;

  // Use session-cached deals query - fetches once per session, cached for subsequent opens
  // Email is extracted from JWT on the server side
  // NOTE: Must be declared before useEffect hooks that depend on isFetching
  const {
    deals: apiDeals,
    isLoading,
    isFetching,
    refetch,
    error,
  } = useDealsQuery({
    requestId,
    enabled: visible,
  });

  // Animation for refresh icon spin with minimum duration
  const spinValue = useRef(new Animated.Value(0)).current;
  const spinAnimation = useRef<Animated.CompositeAnimation | null>(null);
  const [isSpinning, setIsSpinning] = useState(false);
  const minSpinTimeout = useRef<NodeJS.Timeout | null>(null);

  // Start spinning when fetch begins, ensure minimum visible duration
  useEffect(() => {
    if (isFetching && !isSpinning) {
      setIsSpinning(true);
      // Clear any pending stop timeout
      if (minSpinTimeout.current) {
        clearTimeout(minSpinTimeout.current);
        minSpinTimeout.current = null;
      }
    } else if (!isFetching && isSpinning) {
      // Ensure minimum spin time for visual feedback
      minSpinTimeout.current = setTimeout(() => {
        setIsSpinning(false);
        minSpinTimeout.current = null;
      }, ANIMATION.SPIN_DURATION_MS);
    }

    return () => {
      if (minSpinTimeout.current) {
        clearTimeout(minSpinTimeout.current);
      }
    };
  }, [isFetching, isSpinning]);

  // Handle the actual animation
  useEffect(() => {
    if (isSpinning) {
      spinValue.setValue(0);
      spinAnimation.current = Animated.loop(
        Animated.timing(spinValue, {
          toValue: 1,
          duration: ANIMATION.SPIN_DURATION_MS,
          easing: Easing.linear,
          useNativeDriver: Platform.OS !== 'web',
        }),
      );
      spinAnimation.current.start();
    } else {
      if (spinAnimation.current) {
        spinAnimation.current.stop();
        spinAnimation.current = null;
      }
      spinValue.setValue(0);
    }
  }, [isSpinning, spinValue]);

  const spin = spinValue.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '360deg'],
  });

  // Use API deals only - no mock fallback
  const deals = apiDeals;

  // Filter and sort deals when search query or sort options change
  const filteredDeals = useMemo(() => {
    let result = searchDeals(searchQuery, deals);
    // Only sort if a sort option is selected
    if (sortBy) {
      result = sortDeals(result, sortBy, sortOrder);
    }
    return result;
  }, [searchQuery, deals, sortBy, sortOrder]);

  const handleDealSelect = (deal: Deal) => {
    onSelect(deal);
    onClose();
    setSearchQuery(''); // Reset search on close
  };

  // Toggle sort: first click = asc, second = desc, third = off
  const handleSortToggle = (option: SortOption) => {
    if (sortBy !== option) {
      // New sort option: start with ascending
      setSortBy(option);
      setSortOrder('asc');
    } else if (sortOrder === 'asc') {
      // Same option, was asc: switch to desc
      setSortOrder('desc');
    } else {
      // Same option, was desc: turn off sorting
      setSortBy(null);
    }
  };

  const renderDealCard = ({ item }: { item: Deal }) => (
    <TouchableOpacity
      testID={`deal-card-${item.dealRegistrationNumber}`}
      style={styles.dealCard}
      onPress={() => handleDealSelect(item)}
      activeOpacity={0.7}
    >
      <View style={styles.dealCardHeader}>
        <View style={styles.dealCardHeaderLeft}>
          <Text style={styles.dealRegistrationNumber}>
            {item.dealRegistrationNumber}
          </Text>
          <Text style={styles.opportunityName}>{item.opportunityName}</Text>
        </View>
      </View>

      <View style={styles.dealCardBody}>
        <View style={styles.dealInfoRow}>
          <Text style={styles.dealLabel}>Customer:</Text>
          <Text style={styles.dealValue}>{item.endUserCompanyName}</Text>
        </View>

        <View style={styles.dealInfoRow}>
          <Text style={styles.dealLabel}>Representative:</Text>
          <Text style={styles.dealValue}>{item.vertivRepresentativeName}</Text>
        </View>

        <View style={styles.dealInfoRow}>
          <Text style={styles.dealLabel}>Created:</Text>
          <Text style={styles.dealValue}>
            {formatDealCreatedDate(item.opportunityCreatedDate)}
          </Text>
        </View>
      </View>
    </TouchableOpacity>
  );

  const renderTableRow = ({ item }: { item: Deal }) => (
    <TouchableOpacity
      testID={`deal-row-${item.dealRegistrationNumber}`}
      style={styles.tableRow}
      onPress={() => handleDealSelect(item)}
      activeOpacity={0.7}
    >
      <View style={styles.tableCell}>
        <Text style={styles.tableText} numberOfLines={1}>
          {item.dealRegistrationNumber}
        </Text>
      </View>

      <View style={styles.tableCell}>
        <Text style={styles.tableText} numberOfLines={1}>
          {item.endUserCompanyName}
        </Text>
      </View>

      <View style={styles.tableCell}>
        <Text style={styles.tableText} numberOfLines={1}>
          {item.opportunityName}
        </Text>
      </View>

      <View style={styles.tableCell}>
        <Text style={styles.tableText} numberOfLines={1}>
          {item.vertivRepresentativeName}
        </Text>
      </View>

      <View style={styles.tableCell}>
        <Text style={styles.tableText} numberOfLines={1}>
          {formatDealCreatedDate(item.opportunityCreatedDate)}
        </Text>
      </View>
    </TouchableOpacity>
  );

  // Helper to get sort indicator for a button
  const getSortIndicator = (option: SortOption) => {
    if (sortBy !== option) return '';
    return sortOrder === 'asc' ? ' ↑' : ' ↓';
  };

  const renderSortControls = () => (
    <View style={styles.sortContainer}>
      <Text style={styles.sortLabel}>Sort by:</Text>
      <View style={styles.sortButtons}>
        <TouchableOpacity
          style={[
            styles.sortButton,
            sortBy === 'date' && styles.sortButtonActive,
          ]}
          onPress={() => handleSortToggle('date')}
          accessibilityLabel={`Sort by date${getSortIndicator('date')}`}
          accessibilityRole="button"
          accessibilityState={{ selected: sortBy === 'date' }}
        >
          <Text
            style={[
              styles.sortButtonText,
              sortBy === 'date' && styles.sortButtonTextActive,
            ]}
          >
            Date{getSortIndicator('date')}
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[
            styles.sortButton,
            sortBy === 'name' && styles.sortButtonActive,
          ]}
          onPress={() => handleSortToggle('name')}
          accessibilityLabel={`Sort by name${getSortIndicator('name')}`}
          accessibilityRole="button"
          accessibilityState={{ selected: sortBy === 'name' }}
        >
          <Text
            style={[
              styles.sortButtonText,
              sortBy === 'name' && styles.sortButtonTextActive,
            ]}
          >
            Name{getSortIndicator('name')}
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[
            styles.sortButton,
            sortBy === 'company' && styles.sortButtonActive,
          ]}
          onPress={() => handleSortToggle('company')}
          accessibilityLabel={`Sort by company${getSortIndicator('company')}`}
          accessibilityRole="button"
          accessibilityState={{ selected: sortBy === 'company' }}
        >
          <Text
            style={[
              styles.sortButtonText,
              sortBy === 'company' && styles.sortButtonTextActive,
            ]}
          >
            Company{getSortIndicator('company')}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderHeader = () => (
    <>
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <Text style={styles.headerTitle}>{title}</Text>
          <Text style={styles.dealCount}>
            {filteredDeals.length}{' '}
            {filteredDeals.length === 1 ? 'deal' : 'deals'}
          </Text>
        </View>
        <View style={styles.headerRight}>
          <TouchableOpacity
            style={styles.closeButton}
            onPress={onClose}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            accessibilityLabel="Close deal selection"
            accessibilityRole="button"
          >
            <X size={24} color="#333" />
          </TouchableOpacity>
        </View>
      </View>

      <View style={styles.searchContainer}>
        <Search size={20} color="#999" style={styles.searchIcon} />
        <TextInput
          testID="deal-search-input"
          style={styles.searchInput}
          placeholder="Search by name, company, representative, registration #, or opportunity #"
          placeholderTextColor="#999"
          value={searchQuery}
          onChangeText={setSearchQuery}
          autoCorrect={false}
          autoCapitalize="none"
          returnKeyType="search"
          accessibilityLabel="Search deals"
          accessibilityHint="Search by name, company, representative, registration number, or opportunity number"
        />
      </View>

      {renderSortControls()}

      <View style={styles.infoNote}>
        <Info size={14} color="#666" />
        <Text style={styles.infoNoteText}>
          Deal data is updated 1-2 times per day and may not reflect the latest
          changes.
        </Text>
        <TouchableOpacity
          style={[
            styles.refreshButton,
            isSpinning && styles.refreshButtonDisabled,
          ]}
          onPress={() => refetch()}
          disabled={isSpinning}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          accessibilityLabel="Refresh deals"
          accessibilityRole="button"
          accessibilityState={{ disabled: isSpinning }}
        >
          <Animated.View style={{ transform: [{ rotate: spin }] }}>
            <RefreshCw size={20} color={isSpinning ? '#ff6b35' : '#666'} />
          </Animated.View>
        </TouchableOpacity>
      </View>

      {isDesktop && (
        <View style={styles.tableHeader}>
          <View style={styles.tableHeaderCell}>
            <Text style={styles.tableHeaderText}>Deal Number</Text>
          </View>
          <View style={styles.tableHeaderCell}>
            <Text style={styles.tableHeaderText}>Customer Name</Text>
          </View>
          <View style={styles.tableHeaderCell}>
            <Text style={styles.tableHeaderText}>Opportunity Name</Text>
          </View>
          <View style={styles.tableHeaderCell}>
            <Text style={styles.tableHeaderText}>Vertiv Representative</Text>
          </View>
          <View style={styles.tableHeaderCell}>
            <Text style={styles.tableHeaderText}>Created Date</Text>
          </View>
        </View>
      )}
    </>
  );

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
      statusBarTranslucent
    >
      <View style={styles.overlay}>
        <View
          testID="deal-selection-modal"
          style={[
            styles.modal,
            isDesktop && styles.modalDesktop,
            isTablet && styles.modalTablet,
          ]}
        >
          {renderHeader()}

          <View style={styles.listContainer}>
            {isLoading ? (
              <View testID="deal-loading" style={styles.loadingContainer}>
                <ActivityIndicator size="large" color="#ff6b35" />
                <Text style={styles.emptyText}>Loading deals...</Text>
              </View>
            ) : error ? (
              <View testID="deal-error" style={styles.errorContainer}>
                <Text style={styles.errorText}>Failed to load deals</Text>
                <Text style={styles.errorSubtext}>
                  {error.message || 'An unexpected error occurred'}
                </Text>
                <TouchableOpacity
                  style={styles.retryButton}
                  onPress={() => refetch()}
                  accessibilityLabel="Retry loading deals"
                  accessibilityRole="button"
                >
                  <Text style={styles.retryButtonText}>Retry</Text>
                </TouchableOpacity>
              </View>
            ) : filteredDeals.length === 0 ? (
              <View style={styles.emptyContainer}>
                <Text style={styles.emptyText}>
                  {searchQuery
                    ? 'No matching deals found'
                    : 'No deals available'}
                </Text>
                {searchQuery && (
                  <Text style={styles.emptySubtext}>
                    Try adjusting your search criteria
                  </Text>
                )}
              </View>
            ) : (
              <FlatList
                data={filteredDeals}
                keyExtractor={item => item.opportunityId}
                renderItem={isDesktop ? renderTableRow : renderDealCard}
                ListHeaderComponent={
                  <Text style={styles.listHeader}>
                    Select a deal to populate the request
                  </Text>
                }
                contentContainerStyle={styles.listContent}
                showsVerticalScrollIndicator={true}
                nestedScrollEnabled={true}
                removeClippedSubviews={true}
                maxToRenderPerBatch={20}
                windowSize={10}
                initialNumToRender={15}
              />
            )}
          </View>

          <View style={styles.footer}>
            <TouchableOpacity
              testID="deal-modal-cancel-btn"
              style={styles.cancelButton}
              onPress={onClose}
            >
              <Text style={styles.cancelButtonText}>Cancel</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: 12,
    width: '100%',
    maxWidth: 600,
    ...Platform.select({
      web: {
        maxHeight: '90vh' as any,
        display: 'flex' as any,
        flexDirection: 'column' as any,
        boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)' as any,
      },
      default: {
        height: '90%',
      },
    }),
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  modalDesktop: {
    maxWidth: 1000,
  },
  modalTablet: {
    maxWidth: 800,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    backgroundColor: '#fff',
    ...Platform.select({
      web: {
        flexShrink: 0,
        position: 'sticky' as any,
        top: 0,
        zIndex: 10,
      },
    }),
  },
  headerLeft: {
    flex: 1,
  },
  headerRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    marginBottom: 2,
  },
  dealCount: {
    fontSize: 14,
    color: '#666',
    marginTop: 2,
  },
  refreshButton: {
    padding: 8,
    borderRadius: 6,
    backgroundColor: '#f5f5f5',
  },
  refreshButtonDisabled: {
    opacity: 0.6,
  },
  closeButton: {
    padding: 4,
    marginLeft: 8,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 12,
    backgroundColor: '#f8f9fa',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    ...Platform.select({
      web: {
        flexShrink: 0,
        position: 'sticky' as any,
        top: 69, // Height of header
        zIndex: 9,
      },
    }),
  },
  searchIcon: {
    marginRight: 12,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: '#333',
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: '#fff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ddd',
    ...Platform.select({
      web: {
        outlineStyle: 'none' as const,
        outlineWidth: 0,
      },
    }),
  },
  sortContainer: {
    paddingHorizontal: 20,
    paddingVertical: 12,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
    ...Platform.select({
      web: {
        flexShrink: 0,
        position: 'sticky' as any,
        top: 130, // Height of header + search
        zIndex: 8,
      },
    }),
  },
  sortLabel: {
    fontSize: 14,
    color: '#666',
    marginBottom: 8,
    fontWeight: '500',
  },
  sortButtons: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  sortButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#fff',
  },
  sortButtonActive: {
    backgroundColor: '#ff6b35',
    borderColor: '#ff6b35',
  },
  sortButtonText: {
    fontSize: 14,
    color: '#666',
    fontWeight: '500',
  },
  sortButtonTextActive: {
    color: '#fff',
  },
  infoNote: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 10,
    backgroundColor: '#f0f7ff',
    borderBottomWidth: 1,
    borderBottomColor: '#d0e3f5',
    gap: 8,
  },
  infoNoteText: {
    fontSize: 13,
    color: '#555',
    flex: 1,
  },
  listHeader: {
    fontSize: 14,
    color: '#666',
    fontStyle: 'italic',
    paddingHorizontal: 20,
    paddingTop: 12,
    paddingBottom: 8,
  },
  tableHeader: {
    flexDirection: 'row',
    paddingHorizontal: 20,
    paddingVertical: 12,
    backgroundColor: '#f8f9fa',
    borderBottomWidth: 2,
    borderBottomColor: '#ddd',
    ...Platform.select({
      web: {
        flexShrink: 0,
        position: 'sticky' as any,
        top: 189, // Height of header + search + sort
        zIndex: 7,
      },
    }),
  },
  tableHeaderCell: {
    flex: 1,
  },
  tableHeaderText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#333',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  listContainer: {
    flex: 1,
    ...Platform.select({
      web: {
        minHeight: 200,
        overflow: 'auto' as any,
      },
    }),
  },
  listContent: {
    paddingVertical: 8,
    paddingBottom: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 40,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 40,
    paddingHorizontal: 20,
  },
  emptyText: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
    marginTop: 8,
  },
  emptySubtext: {
    fontSize: 14,
    color: '#999',
    textAlign: 'center',
    marginTop: 8,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 40,
    paddingHorizontal: 20,
  },
  errorText: {
    fontSize: 16,
    color: '#d32f2f',
    textAlign: 'center',
    fontWeight: '600',
  },
  errorSubtext: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    marginTop: 8,
    marginBottom: 16,
  },
  retryButton: {
    paddingHorizontal: 24,
    paddingVertical: 10,
    borderRadius: 6,
    backgroundColor: '#ff6b35',
  },
  retryButtonText: {
    fontSize: 14,
    color: '#fff',
    fontWeight: '600',
  },
  // Card styles (for mobile/tablet)
  dealCard: {
    marginHorizontal: 16,
    marginVertical: 8,
    padding: 16,
    backgroundColor: '#fff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e0e0e0',
    ...Platform.select({
      web: {
        cursor: 'pointer' as any,
      },
    }),
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  dealCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  dealCardHeaderLeft: {
    flex: 1,
    marginRight: 8,
  },
  dealRegistrationNumber: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 4,
  },
  opportunityName: {
    fontSize: 14,
    color: '#666',
    fontWeight: '500',
  },
  dealCardBody: {
    gap: 8,
  },
  dealInfoRow: {
    flexDirection: 'row',
  },
  dealLabel: {
    fontSize: 14,
    color: '#666',
    fontWeight: '500',
    width: 80,
    flexShrink: 0,
  },
  dealValue: {
    fontSize: 14,
    color: '#333',
    flex: 1,
  },
  // Table styles (for desktop)
  tableRow: {
    flexDirection: 'row',
    paddingHorizontal: 20,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#f0f0f0',
    ...Platform.select({
      web: {
        cursor: 'pointer' as any,
        ':hover': {
          backgroundColor: '#f8f9fa',
        },
      },
    }),
  },
  tableCell: {
    flex: 1,
    justifyContent: 'center',
  },
  tableText: {
    fontSize: 14,
    color: '#333',
  },
  tablePrimaryText: {
    fontWeight: '500',
    marginBottom: 2,
  },
  tableSecondaryText: {
    fontSize: 12,
    color: '#666',
  },
  footer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
    backgroundColor: '#fff',
    ...Platform.select({
      web: {
        flexShrink: 0,
        position: 'sticky' as any,
        bottom: 0,
      },
    }),
  },
  cancelButton: {
    paddingHorizontal: 24,
    paddingVertical: 10,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#ddd',
    backgroundColor: '#fff',
  },
  cancelButtonText: {
    fontSize: 16,
    color: '#666',
    fontWeight: '500',
  },
});
